import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { adminAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { 
  BarChart3, Globe, Monitor, Users, Clock, TrendingUp, 
  Smartphone, Tablet, Laptop, MapPin, ExternalLink, Loader2,
  Eye, Calendar, RefreshCw
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const AnalyticsDashboard = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [days, setDays] = useState(30);

  useEffect(() => {
    fetchAnalytics();
  }, [days]);

  const fetchAnalytics = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      const response = await adminAPI.getAnalytics(token, days);
      if (response.success) {
        setAnalytics(response.data);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load analytics data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const getDeviceIcon = (device) => {
    switch (device?.toLowerCase()) {
      case 'mobile':
        return <Smartphone size={16} className="text-orange-500" />;
      case 'tablet':
        return <Tablet size={16} className="text-orange-500" />;
      default:
        return <Laptop size={16} className="text-orange-500" />;
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const getPercentage = (value, total) => {
    if (!total) return '0%';
    return ((value / total) * 100).toFixed(1) + '%';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-orange-500" size={40} />
      </div>
    );
  }

  const overview = analytics?.overview || {};
  const pageViews = analytics?.pageViews || {};
  const geography = analytics?.geography || {};
  const technology = analytics?.technology || {};
  const traffic = analytics?.traffic || {};
  const recentVisitors = analytics?.recentVisitors || [];
  const topIPs = traffic?.topIPs || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500/20 to-orange-600/20 flex items-center justify-center">
            <BarChart3 size={20} className="text-orange-500" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Visitor Analytics</h3>
            <p className="text-sm text-gray-400">Track your portfolio visitors</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-3 py-2 bg-zinc-900 border border-orange-500/20 text-white rounded-lg text-sm"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <Button
            onClick={() => fetchAnalytics(true)}
            disabled={refreshing}
            variant="outline"
            size="sm"
            className="border-orange-500/30 text-orange-500 hover:bg-orange-500/10"
          >
            {refreshing ? (
              <Loader2 className="animate-spin" size={16} />
            ) : (
              <RefreshCw size={16} />
            )}
          </Button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="glass border border-orange-500/20 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500/20 to-orange-600/20 flex items-center justify-center">
              <Eye size={24} className="text-orange-500" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Total Visits</p>
              <p className="text-2xl font-bold text-white">{formatNumber(overview.totalVisits)}</p>
            </div>
          </div>
        </div>
        <div className="glass border border-orange-500/20 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500/20 to-blue-600/20 flex items-center justify-center">
              <Users size={24} className="text-blue-500" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Unique Visitors</p>
              <p className="text-2xl font-bold text-white">{formatNumber(overview.uniqueVisitors)}</p>
            </div>
          </div>
        </div>
        <div className="glass border border-orange-500/20 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500/20 to-green-600/20 flex items-center justify-center">
              <TrendingUp size={24} className="text-green-500" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Sessions</p>
              <p className="text-2xl font-bold text-white">{formatNumber(overview.uniqueSessions)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Accordion Sections */}
      <Accordion type="multiple" defaultValue={["pages", "geography"]} className="space-y-4">
        
        {/* Page Views */}
        <AccordionItem value="pages" className="glass border border-orange-500/20 rounded-2xl px-6">
          <AccordionTrigger className="text-lg font-semibold text-white hover:text-orange-500">
            <div className="flex items-center gap-3">
              <Eye size={20} className="text-orange-500" />
              Page Views
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-4">
            <div className="space-y-3">
              {Object.entries(pageViews).length > 0 ? (
                Object.entries(pageViews)
                  .sort((a, b) => b[1] - a[1])
                  .map(([page, count]) => (
                    <div key={page} className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-orange-500" />
                        <span className="text-gray-300">{page || 'Homepage'}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="w-32 h-2 bg-zinc-800 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-orange-500 to-orange-600 rounded-full"
                            style={{ width: getPercentage(count, overview.totalVisits) }}
                          />
                        </div>
                        <span className="text-white font-medium w-16 text-right">{count}</span>
                      </div>
                    </div>
                  ))
              ) : (
                <p className="text-gray-500 text-center py-4">No page view data yet</p>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Geographic Data */}
        <AccordionItem value="geography" className="glass border border-orange-500/20 rounded-2xl px-6">
          <AccordionTrigger className="text-lg font-semibold text-white hover:text-orange-500">
            <div className="flex items-center gap-3">
              <Globe size={20} className="text-orange-500" />
              Geographic Data
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Countries */}
              <div>
                <h4 className="text-gray-400 text-sm font-medium mb-3 flex items-center gap-2">
                  <Globe size={16} />
                  Top Countries
                </h4>
                <div className="space-y-2">
                  {Object.entries(geography.countries || {}).length > 0 ? (
                    Object.entries(geography.countries)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 10)
                      .map(([country, count]) => (
                        <div key={country} className="flex items-center justify-between p-2 bg-zinc-900/50 rounded-lg">
                          <span className="text-gray-300">{country}</span>
                          <span className="text-orange-500 font-medium">{count}</span>
                        </div>
                      ))
                  ) : (
                    <p className="text-gray-500 text-sm">No country data yet</p>
                  )}
                </div>
              </div>
              
              {/* Cities */}
              <div>
                <h4 className="text-gray-400 text-sm font-medium mb-3 flex items-center gap-2">
                  <MapPin size={16} />
                  Top Cities
                </h4>
                <div className="space-y-2">
                  {Object.entries(geography.cities || {}).length > 0 ? (
                    Object.entries(geography.cities)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 10)
                      .map(([city, count]) => (
                        <div key={city} className="flex items-center justify-between p-2 bg-zinc-900/50 rounded-lg">
                          <span className="text-gray-300">{city}</span>
                          <span className="text-orange-500 font-medium">{count}</span>
                        </div>
                      ))
                  ) : (
                    <p className="text-gray-500 text-sm">No city data yet</p>
                  )}
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Technology */}
        <AccordionItem value="technology" className="glass border border-orange-500/20 rounded-2xl px-6">
          <AccordionTrigger className="text-lg font-semibold text-white hover:text-orange-500">
            <div className="flex items-center gap-3">
              <Monitor size={20} className="text-orange-500" />
              Technology & Devices
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Browsers */}
              <div>
                <h4 className="text-gray-400 text-sm font-medium mb-3">Browsers</h4>
                <div className="space-y-2">
                  {Object.entries(technology.browsers || {}).length > 0 ? (
                    Object.entries(technology.browsers)
                      .sort((a, b) => b[1] - a[1])
                      .map(([browser, count]) => (
                        <div key={browser} className="flex items-center justify-between p-2 bg-zinc-900/50 rounded-lg">
                          <span className="text-gray-300">{browser}</span>
                          <span className="text-orange-500 font-medium">{count}</span>
                        </div>
                      ))
                  ) : (
                    <p className="text-gray-500 text-sm">No data yet</p>
                  )}
                </div>
              </div>
              
              {/* Operating Systems */}
              <div>
                <h4 className="text-gray-400 text-sm font-medium mb-3">Operating Systems</h4>
                <div className="space-y-2">
                  {Object.entries(technology.os || {}).length > 0 ? (
                    Object.entries(technology.os)
                      .sort((a, b) => b[1] - a[1])
                      .map(([os, count]) => (
                        <div key={os} className="flex items-center justify-between p-2 bg-zinc-900/50 rounded-lg">
                          <span className="text-gray-300">{os}</span>
                          <span className="text-orange-500 font-medium">{count}</span>
                        </div>
                      ))
                  ) : (
                    <p className="text-gray-500 text-sm">No data yet</p>
                  )}
                </div>
              </div>
              
              {/* Devices */}
              <div>
                <h4 className="text-gray-400 text-sm font-medium mb-3">Devices</h4>
                <div className="space-y-2">
                  {Object.entries(technology.devices || {}).length > 0 ? (
                    Object.entries(technology.devices)
                      .sort((a, b) => b[1] - a[1])
                      .map(([device, count]) => (
                        <div key={device} className="flex items-center justify-between p-2 bg-zinc-900/50 rounded-lg">
                          <div className="flex items-center gap-2">
                            {getDeviceIcon(device)}
                            <span className="text-gray-300">{device}</span>
                          </div>
                          <span className="text-orange-500 font-medium">{count}</span>
                        </div>
                      ))
                  ) : (
                    <p className="text-gray-500 text-sm">No data yet</p>
                  )}
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Traffic Sources */}
        <AccordionItem value="traffic" className="glass border border-orange-500/20 rounded-2xl px-6">
          <AccordionTrigger className="text-lg font-semibold text-white hover:text-orange-500">
            <div className="flex items-center gap-3">
              <ExternalLink size={20} className="text-orange-500" />
              Traffic Sources
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-4">
            <div className="space-y-3">
              <div>
                <h4 className="text-gray-400 text-sm font-medium mb-2">Top Referrers</h4>
                {Object.entries(traffic.referrers || {}).length > 0 ? (
                  Object.entries(traffic.referrers)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 10)
                    .map(([referrer, count]) => (
                      <div key={referrer} className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <ExternalLink size={16} className="text-gray-500" />
                          <span className="text-gray-300 truncate max-w-xs">{referrer}</span>
                        </div>
                        <span className="text-orange-500 font-medium">{count}</span>
                      </div>
                    ))
                ) : (
                  <p className="text-gray-500 text-center py-2">No referrer data yet</p>
                )}
              </div>

              <div>
                <h4 className="text-gray-400 text-sm font-medium mb-2">Top IP Addresses</h4>
                {Object.entries(topIPs).length > 0 ? (
                  Object.entries(topIPs)
                    .sort((a, b) => b[1] - a[1])
                    .map(([ip, count]) => (
                      <div key={ip} className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-lg">
                        <span className="text-gray-300 font-mono text-sm">{ip}</span>
                        <span className="text-orange-500 font-medium">{count}</span>
                      </div>
                    ))
                ) : (
                  <p className="text-gray-500 text-center py-2">No IP data yet</p>
                )}
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Daily Visits Chart */}
        <AccordionItem value="daily" className="glass border border-orange-500/20 rounded-2xl px-6">
          <AccordionTrigger className="text-lg font-semibold text-white hover:text-orange-500">
            <div className="flex items-center gap-3">
              <Calendar size={20} className="text-orange-500" />
              Daily Visits
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-4">
            <div className="space-y-3">
              {Object.entries(traffic.dailyVisits || {}).length > 0 ? (
                <div className="space-y-2">
                  {Object.entries(traffic.dailyVisits)
                    .slice(-14)
                    .map(([date, count]) => {
                      const maxCount = Math.max(...Object.values(traffic.dailyVisits));
                      const width = maxCount > 0 ? (count / maxCount) * 100 : 0;
                      return (
                        <div key={date} className="flex items-center gap-4">
                          <span className="text-gray-400 text-sm w-24 shrink-0">{date}</span>
                          <div className="flex-1 h-6 bg-zinc-800 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-orange-500 to-orange-600 rounded-full flex items-center justify-end pr-2"
                              style={{ width: `${Math.max(width, 10)}%` }}
                            >
                              <span className="text-white text-xs font-medium">{count}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No daily visit data yet</p>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Recent Visitors */}
        <AccordionItem value="recent" className="glass border border-orange-500/20 rounded-2xl px-6">
          <AccordionTrigger className="text-lg font-semibold text-white hover:text-orange-500">
            <div className="flex items-center gap-3">
              <Clock size={20} className="text-orange-500" />
              Recent Visitors
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-4">
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {recentVisitors.length > 0 ? (
                recentVisitors.map((visitor, index) => (
                  <div key={index} className="p-3 bg-zinc-900/50 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          {getDeviceIcon(visitor.device)}
                          <span className="text-gray-300 font-medium">{visitor.browser} on {visitor.os}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500 flex-wrap">
                          {visitor.city && visitor.country && (
                            <span className="flex items-center gap-1">
                              <MapPin size={12} />
                              {visitor.city}, {visitor.country}
                            </span>
                          )}
                          {visitor.ip && (
                            <span className="font-mono text-xs text-orange-300 bg-orange-500/10 px-2 py-0.5 rounded">
                              {visitor.ip}
                            </span>
                          )}
                          <span>Page: {visitor.page || '/'}</span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className="text-xs text-gray-500">
                          {visitor.timestamp ? new Date(visitor.timestamp).toLocaleString() : 'Unknown'}
                        </span>
                        {visitor.count > 1 && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-200 font-semibold">
                            x{visitor.count}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">No recent visitors yet</p>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default AnalyticsDashboard;
